import { ApiError, apiFetch, parseErrorDetail } from "@/api/fetch";
import { useQuery } from "@tanstack/react-query";
import type { FastCell, FastColumn, FastDataset, FastDatasetSummary, FastEvent, FastFigure, FastProgress, FastQuota } from "./types";
import type { RowSource, SourceKey } from "./components/SourceReview";
import { anonTokenFor, clearAnonClaim, getAnon, readEvents } from "./anonymous";

const BASE = "/fast-datasets";
export const fastDatasetsKey = ["fast-datasets"] as const;

/** The plan has no datasets left to build: the quota says which plan and when more come. */
export class FastQuotaError extends ApiError {
  readonly quota: FastQuota;

  constructor(message: string, quota: FastQuota) {
    super(message, 403);
    this.name = "FastQuotaError";
    this.quota = quota;
  }
}

const failure = async (response: Response): Promise<ApiError> => {
  if (response.status === 403) {
    const body: unknown = await response.clone().json().catch(() => null);
    if (body && typeof body === "object" && "quota" in body) {
      const { detail, quota } = body as { detail?: unknown; quota: FastQuota };
      return new FastQuotaError(typeof detail === "string" ? detail : "No datasets left on your plan.", quota);
    }
  }
  return new ApiError(await parseErrorDetail(response), response.status);
};

const json = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const response = await apiFetch(path, init);
  if (!response.ok) throw await failure(response);
  return response.json() as Promise<T>;
};

export const listFastDatasets = async (): Promise<FastDatasetSummary[]> =>
  (await json<{ datasets: FastDatasetSummary[] }>(BASE)).datasets;

export const fastQuotaKey = [...fastDatasetsKey, "quota"] as const;

export const getFastQuota = (): Promise<FastQuota> => json(`${BASE}/quota`);

/** How many datasets the user's plan builds, and how many are left. */
export const useFastQuota = (enabled = true) =>
  useQuery({ queryKey: fastQuotaKey, queryFn: getFastQuota, enabled, staleTime: 30_000 });

/** A dataset this browser built logged out reads with its claim token until it is claimed. */
const withClaim = async <T extends FastDataset | FastProgress>(id: string, progress: boolean): Promise<T | null> => {
  const token = anonTokenFor(id);
  if (!token) return null;
  const found = await getAnon<T>(token, progress);
  if (!found) clearAnonClaim();
  return found;
};

export const getFastDataset = async (id: string): Promise<FastDataset> =>
  (await withClaim<FastDataset>(id, false)) ?? json(`${BASE}/${encodeURIComponent(id)}`);

/** The build's status and row count, without the rows. */
export const getFastProgress = async (id: string): Promise<FastProgress> =>
  (await withClaim<FastProgress>(id, true)) ?? json(`${BASE}/${encodeURIComponent(id)}/progress`);

/** Smart sort: the rows with a value in the column, most of the criterion first. */
export const rankFastColumn = (id: string, key: string, criterion: string) =>
  json<{ criterion: string; order: string[] }>(`${BASE}/${encodeURIComponent(id)}/columns/${encodeURIComponent(key)}/rank`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ criterion: criterion || null }),
  });

/** The column's distinct values as read, each with its current label and a proposed one. */
export const suggestFastNormalization = (id: string, key: string) =>
  json<{ values: { value: string; count: number; current: string }[]; labels: string[]; groups: string[]; reasons: string[] }>(
    `${BASE}/${encodeURIComponent(id)}/columns/${encodeURIComponent(key)}/normalize`,
    { method: "POST" },
  );

/** Each value as read takes the label the user approved. */
export const applyFastNormalization = (id: string, key: string, groups: { value: string; label: string; reason: string }[]) =>
  json<{ changed: number }>(`${BASE}/${encodeURIComponent(id)}/columns/${encodeURIComponent(key)}/normalize`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ groups }),
  });

export type FastMethods = { methods: string | null; methods_status: FastDataset["methods_status"] };

export const getFastMethods = (id: string) => json<FastMethods>(`${BASE}/${encodeURIComponent(id)}/methods`);

/** Writes the dataset's methods section again, in the background. */
export const writeFastMethods = (id: string) =>
  json<{ methods_status: "writing" }>(`${BASE}/${encodeURIComponent(id)}/methods`, { method: "POST" });

export const deleteFastDataset = async (id: string): Promise<void> => {
  const response = await apiFetch(`${BASE}/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!response.ok) throw new ApiError(await parseErrorDetail(response), response.status);
};

export type ColumnAnswer = { column: FastColumn; cells: Record<string, FastCell>; figures?: Record<string, FastFigure> };

export type ColumnInput = { label: string; description: string; value_type?: FastColumn["value_type"]; options?: string[] };

export const getSharedFastDataset = (token: string): Promise<FastDataset> =>
  json(`${BASE}/shared/${encodeURIComponent(token)}`);

/** Removes rows; More rows will not bring them back. */
export const deleteFastRows = (id: string, rowKeys: string[]) =>
  json<{ deleted: number }>(`${BASE}/${encodeURIComponent(id)}/rows/delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ row_keys: rowKeys }),
  });

export const clearFastCells = (id: string, cells: { row_key: string; key: string }[]) =>
  json<{ cleared: number }>(`${BASE}/${encodeURIComponent(id)}/cells/clear`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ cells }),
  });

export const shareFastDataset = (id: string) =>
  json<{ token: string }>(`${BASE}/${encodeURIComponent(id)}/share`, { method: "POST" });

export const unshareFastDataset = async (id: string): Promise<void> => {
  const response = await apiFetch(`${BASE}/${encodeURIComponent(id)}/share`, { method: "DELETE" });
  if (!response.ok) throw new ApiError(await parseErrorDetail(response), response.status);
};

export const sharedFastUrl = (token: string) => `${window.location.origin}/datasets/shared/${encodeURIComponent(token)}`;

/** Downloads the dataset as CSV: each column's value, then each column's exact quote. */
/** Downloads the CSV, or with images a ZIP of the CSV and the rows' figures (images/), whose files the CSV names. */
export const downloadFastCsv = async (target: { id: string } | { token: string }, question: string, images = false) => {
  const file = images ? "export.zip" : "export.csv";
  const path = "token" in target
    ? `${BASE}/shared/${encodeURIComponent(target.token)}/${file}`
    : `${BASE}/${encodeURIComponent(target.id)}/${file}`;
  const response = await apiFetch(path);
  if (!response.ok) throw new ApiError(await parseErrorDetail(response), response.status);
  const url = URL.createObjectURL(await response.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = `${question.toLowerCase().match(/[a-z0-9]+/g)?.join("_").slice(0, 60) || "dataset"}.${images ? "zip" : "csv"}`;
  a.click();
  URL.revokeObjectURL(url);
};

/** Adds a column and answers it for every row from the passages the build already read. */
export const addFastColumn = (id: string, input: ColumnInput) =>
  json<ColumnAnswer>(`${BASE}/${encodeURIComponent(id)}/columns`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });

/** Renames or redescribes a column and answers it again for every row. */
export const updateFastColumn = (id: string, key: string, input: ColumnInput) =>
  json<ColumnAnswer>(
    `${BASE}/${encodeURIComponent(id)}/columns/${encodeURIComponent(key)}`,
    { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(input) },
  );

/** Saves the column order; columns not in keys keep their places after them. */
export const reorderFastColumns = (id: string, keys: string[]) =>
  json<{ keys: string[] }>(`${BASE}/${encodeURIComponent(id)}/column-order`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ keys }),
  });

export const deleteFastColumn = async (id: string, key: string): Promise<void> => {
  const response = await apiFetch(`${BASE}/${encodeURIComponent(id)}/columns/${encodeURIComponent(key)}`, {
    method: "DELETE",
  });
  if (!response.ok) throw new ApiError(await parseErrorDetail(response), response.status);
};

/** Reads a server-sent event stream of the dataset API until it ends. */
const stream = async (path: string, body: unknown, onEvent: (event: FastEvent) => void) => {
  const response = await apiFetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok || !response.body) throw await failure(response);
  await readEvents(response, onEvent);
};

export const streamFastBuild = (
  input: { question: string; max_rows: number; papers: number | null },
  onEvent: (event: FastEvent) => void,
) => stream(BASE, input, onEvent);

/** A column as the user left it in review; no key for a column they added. */
export type ReviewedColumn = ColumnInput & { key: string | null; required: boolean };

/** Creates a dataset with the columns the builder proposes, for the user to review before any paper is read. */
export const planFastDataset = (input: { question: string; max_rows: number; papers: number | null }) =>
  json<FastDataset>(`${BASE}/plan`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });

/** Auto-approve: the server proposes the columns, approves them as proposed and builds, in one stream (dataset, plan,
 * then the build's events). */
export const streamFastAutoBuild = (
  input: { question: string; max_rows: number; papers: number | null },
  onEvent: (event: FastEvent) => void,
) => stream(`${BASE}/plan`, { ...input, auto_approve: true }, onEvent);

/** Builds a dataset in review from its reviewed columns, in their order. */
/** The sources to search, whether papers are among them, and whose records are the rows, as the user confirmed them. */
export type ReviewedSources = { sources: SourceKey[]; row_source: RowSource; papers: boolean };

export const streamFastReviewedBuild = (
  id: string,
  columns: ReviewedColumn[],
  sources: ReviewedSources | null,
  onEvent: (event: FastEvent) => void,
) => stream(`${BASE}/${encodeURIComponent(id)}/build`, { columns, ...sources }, onEvent);

export const streamFastMore = (id: string, onEvent: (event: FastEvent) => void) =>
  stream(`${BASE}/${encodeURIComponent(id)}/more`, undefined, onEvent);

/** New searches for relevant papers the dataset does not have, written from what it found and missed. */
export const streamFastBroaden = (id: string, onEvent: (event: FastEvent) => void) =>
  stream(`${BASE}/${encodeURIComponent(id)}/broaden`, undefined, onEvent);

/** Searches again now, as the weekly monitor does, and adds rows from papers the dataset has not read. */
export const streamFastMonitorRun = (id: string, onEvent: (event: FastEvent) => void) =>
  stream(`${BASE}/${encodeURIComponent(id)}/monitor/run`, undefined, onEvent);

export const setFastMonitor = (id: string, enabled: boolean) =>
  json<FastDataset>(`${BASE}/${encodeURIComponent(id)}/monitor`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ enabled }),
  });

/** The user's Fast datasets, for the left panel. Polls while one is being built. */
export const useFastDatasets = (enabled = true) =>
  useQuery({
    queryKey: fastDatasetsKey,
    queryFn: listFastDatasets,
    enabled,
    refetchInterval: (query) =>
      query.state.data?.some((d) => d.status === "running") ? 4_000 : false,
  });
